import {
  Tooltip,
  createStyles,
  ActionIcon,
  Text,
  Stack,
  Button,
  Group,
  Popover,
  PopoverProps,
} from '@mantine/core';
import { AnimatePresence } from 'framer-motion';
import { compact, size } from 'lodash/fp';
import React, { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useConfig } from '@portals/api/organizations';
import {
  usePermissionAccess,
  isCustomerFeatureAll,
  isCustomerFeatureLabOnly,
} from '@portals/framework';
import { ReactComponent as Back } from '@portals/icons/linear/arrow-left.svg';
import { ReactComponent as Book } from '@portals/icons/linear/book-1.svg';
import { ReactComponent as Glossary } from '@portals/icons/linear/book-a.svg';
import { ReactComponent as DocumentText } from '@portals/icons/linear/document-text.svg';
import { ReactComponent as LifeBuoy } from '@portals/icons/linear/lifebuoy.svg';
import { ReactComponent as QuestionCircleIcon } from '@portals/icons/linear/question-circle.svg';
import { ReactComponent as Task } from '@portals/icons/linear/task.svg';
import { ReactComponent as VideoSquare } from '@portals/icons/linear/video-square.svg';
import { isGettingStartedToursDone } from '@portals/utils';

import { SuccessCenterItems } from './SuccessCenterItems';

export interface MenuItemType {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

export function SuccessCenter() {
  const { classes, theme } = useStyles();

  const [isOpen, setIsOpen] = useState(false);

  const [showSuccessCenterItems, setShowSuccessCenterItems] = useState(true);

  const navigate = useNavigate();

  const config = useConfig();

  const { isAdmin } = usePermissionAccess();

  const showSetupOrganizationItem =
    isAdmin &&
    (config.data?.onboarding_tours?.dismiss ||
      isGettingStartedToursDone(config.data)) &&
    (isCustomerFeatureAll(config.data?.portal_capabilities.onboarding_guides) ||
      isCustomerFeatureLabOnly(
        config.data?.portal_capabilities.onboarding_guides,
        config.data?.organization.lab
      ));

  const successCenterMenuItems: Array<MenuItemType> = compact([
    showSetupOrganizationItem && {
      icon: (
        <Task color={theme.colors.indigo_accent[2]} width={32} height={32} />
      ),
      onClick: () => {
        setIsOpen(false);
        navigate('setup');
      },
      label: 'Setup your account',
    },
    {
      icon: (
        <DocumentText
          color={theme.colors.indigo_accent[2]}
          width={32}
          height={32}
        />
      ),
      onClick: () => window.open('https://docs.xyte.io/', '_blank'),
      label: 'Documentation',
    },
    {
      icon: (
        <VideoSquare
          color={theme.colors.indigo_accent[2]}
          width={32}
          height={32}
        />
      ),
      onClick: () =>
        window.open(
          'https://www.youtube.com/playlist?list=PLqmvZivrF9W77RL6DZTfrKtgtd-o1K5Ok',
          '_blank'
        ),
      label: 'Videos',
    },
    {
      icon: (
        <Glossary
          color={theme.colors.indigo_accent[2]}
          width={32}
          height={32}
        />
      ),
      onClick: () => window.open('https://www.xyte.io/glossary', '_blank'),
      label: 'Glossary',
    },
    size(config.data?.organization.partner_manuals) > 0 && {
      icon: (
        <Book color={theme.colors.indigo_accent[2]} width={32} height={32} />
      ),
      label: 'Manufacturer manuals',
      onClick: () => setShowSuccessCenterItems(false),
    },
  ]);

  const onOpenMenu = (value: boolean) => {
    setIsOpen(value);
    setShowSuccessCenterItems(true);
  };

  return (
    <Popover
      position="bottom-end"
      opened={isOpen}
      onChange={onOpenMenu}
      closeOnClickOutside
      offset={20}
      radius="lg"
      shadow="md"
      width={400}
      styles={popoverStyles}
    >
      <Popover.Target>
        <Tooltip label="Success center">
          <ActionIcon
            size="lg"
            variant="transparent"
            onClick={() => setIsOpen((isOpened) => !isOpened)}
          >
            <QuestionCircleIcon
              width={24}
              height={24}
              color={
                isOpen
                  ? theme.colors.indigo_accent[2]
                  : theme.colors.blue_gray[6]
              }
            />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>

      <Popover.Dropdown px="xl" pb="xl" h={400}>
        <Stack spacing={4} h="100%">
          <Group
            position="center"
            className={classes.titleContainer}
            pt="md"
            pb="xs"
          >
            {!showSuccessCenterItems && (
              <ActionIcon className={classes.backButton}>
                <Back
                  width={24}
                  height={24}
                  onClick={() => setShowSuccessCenterItems(true)}
                />
              </ActionIcon>
            )}

            <Text color="gray.8" weight={600} ta="center">
              {showSuccessCenterItems
                ? 'Success center'
                : 'Manufacturer manuals'}
            </Text>
          </Group>

          <Stack spacing="xl" h="100%" justify="space-between">
            <AnimatePresence>
              <SuccessCenterItems
                successCenterMenuItems={successCenterMenuItems}
                showSuccessCenterItems={showSuccessCenterItems}
                partnerManuals={config.data?.organization.partner_manuals}
              />
            </AnimatePresence>

            {showSuccessCenterItems && (
              <Button
                leftIcon={<LifeBuoy width={18} height={18} />}
                variant="default"
                onClick={() => window.open('mailto:support@xyte.io', '_blank')}
                px="xl"
                py="sm"
                size="md"
              >
                Contact technical support
              </Button>
            )}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

const useStyles = createStyles(() => ({
  titleContainer: {
    position: 'sticky',
    top: -15,
    zIndex: 2,
    background: 'white',
  },

  backButton: {
    position: 'absolute',
    left: 0,
  },
}));

const popoverStyles: PopoverProps['styles'] = () => ({
  dropdown: {
    overflow: 'auto',
  },
});
